import { Injectable } from '@angular/core';
import type { Params } from '@angular/router';
import { Action, Selector, State, Store, type StateContext } from '@ngxs/store';
import { NavigationControlModels } from '@medsurf/flat-models';
import { NavigationControlActions } from '@medsurf/flat-actions';

export interface NavigationControlStateModel {
  slugs: NavigationControlModels.PathModel | null;
  currentURL: string;
  queryParams: Params | null;
  loadTrainings: boolean;
  loadRestrictedAndHidden: boolean;
  showSearch: boolean;
  showClose: boolean;
  isPrinting: boolean;
}

@State<NavigationControlStateModel>({
  name: 'navigationControl',
  defaults: {
    slugs: null,
    currentURL: '',
    queryParams: null,
    loadTrainings: false,
    loadRestrictedAndHidden: false,
    showSearch: true,
    showClose: false,
    isPrinting: false,
  }
})
@Injectable()
export class NavigationControlState {
  static order = ['eLearning', 'subject', 'chapter', 'block', 'slide'];

  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  @Selector([NavigationControlState])
  static slugs$(state: NavigationControlStateModel): NavigationControlModels.PathModel | null {
    return state.slugs;
  }

  //<editor-fold desc="Page">

  @Selector([NavigationControlState.slugs$])
  static pageSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs
      && slugs.type === NavigationControlModels.PathModelType.enum.PAGE
      ? (slugs as NavigationControlModels.PagePathModel).page || null
      : null;
  }

  //</editor-fold>

  //<editor-fold desc="Slide">

  @Selector([NavigationControlState.slugs$])
  static elearningSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs && ((<string[]>[NavigationControlModels.PathModelType.enum.SLIDE, NavigationControlModels.PathModelType.enum.TRAINING]).includes(slugs.type))
      ? (slugs as NavigationControlModels.SlidePathModel).eLearning || null
      : null;
  }

  @Selector([NavigationControlState.slugs$])
  static subjectSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs && ((<string[]>[NavigationControlModels.PathModelType.enum.SLIDE, NavigationControlModels.PathModelType.enum.TRAINING]).includes(slugs.type))
      ? (slugs as NavigationControlModels.SlidePathModel).subject || null
      : null;
  }

  @Selector([NavigationControlState.slugs$])
  static chapterSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs && ((<string[]>[NavigationControlModels.PathModelType.enum.SLIDE, NavigationControlModels.PathModelType.enum.TRAINING]).includes(slugs.type))
      ? (slugs as NavigationControlModels.SlidePathModel).chapter || null
      : null;
  }

  @Selector([NavigationControlState.slugs$])
  static blockSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs && ((<string[]>[NavigationControlModels.PathModelType.enum.SLIDE, NavigationControlModels.PathModelType.enum.TRAINING]).includes(slugs.type))
      ? (slugs as NavigationControlModels.SlidePathModel).block || null
      : null;
  }

  @Selector([NavigationControlState.slugs$])
  static slideSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs && ((<string[]>[NavigationControlModels.PathModelType.enum.SLIDE, NavigationControlModels.PathModelType.enum.TRAINING]).includes(slugs.type))
      ? (slugs as NavigationControlModels.SlidePathModel).slide || null
      : null;
  }

  @Selector([NavigationControlState.slugs$])
  static nodeIdSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs && ((<string[]>[NavigationControlModels.PathModelType.enum.NODE_ID]).includes(slugs.type))
      ? (slugs as NavigationControlModels.NodeIdPathModel).id || null
      : null;
  }

  @Selector([NavigationControlState.slugs$])
  static slideIdSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs && ((<string[]>[NavigationControlModels.PathModelType.enum.SLIDE_ID]).includes(slugs.type))
      ? (slugs as NavigationControlModels.SlideSlideIdPathModel).id || null
      : null;
  }

  @Selector([NavigationControlState.elearningSlug$])
  static elearningUrl$(elearningSlug: string | null) {
    return elearningSlug || null;
  }

  @Selector([
    NavigationControlState.elearningUrl$,
    NavigationControlState.subjectSlug$
  ])
  static subjectUrl$(elearningUrl: string | null,
                     subjectSlug: string | null) {
    return elearningUrl && subjectSlug ? (elearningUrl + '/' + subjectSlug) || null : null;
  }

  @Selector([
    NavigationControlState.subjectUrl$,
    NavigationControlState.chapterSlug$
  ])
  static chapterUrl$(subjectUrl: string | null,
                     chapterSlug: string | null) {
    return subjectUrl && chapterSlug ? (subjectUrl + '/' + chapterSlug) || null : null;
  }

  @Selector([
    NavigationControlState.chapterUrl$,
    NavigationControlState.blockSlug$
  ])
  static blockUrl$(chapterUrl: string | null,
                   blockSlug: string | null) {
    return chapterUrl && blockSlug ? (chapterUrl + '/' + blockSlug) || null : null;
  }

  @Selector([
    NavigationControlState.blockUrl$,
    NavigationControlState.slideSlug$
  ])
  static slideUrl$(blockUrl: string | null,
                   slideSlug: string | null) {
    return blockUrl && slideSlug ? (blockUrl + '/' + slideSlug) || null : null;
  }

  //</editor-fold>

  //<editor-fold desc="User">

  @Selector([NavigationControlState.slugs$])
  static userIdSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs && ((<string[]>[NavigationControlModels.PathModelType.enum.USER_ID]).includes(slugs.type))
      ? (slugs as NavigationControlModels.UserIdPathModel).id || null
      : null;
  }

  //</editor-fold>

  //<editor-fold desc="Copyright">

  @Selector([NavigationControlState.slugs$])
  static copyrightIdSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs && ((<string[]>[NavigationControlModels.PathModelType.enum.COPYRIGHT_ID]).includes(slugs.type))
      ? (slugs as NavigationControlModels.CopyrightIdPathModel).id || null
      : null;
  }

  //</editor-fold>

  //<editor-fold desc="Modality">

  @Selector([NavigationControlState.slugs$])
  static modalityIdSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs && ((<string[]>[NavigationControlModels.PathModelType.enum.MODALITY_ID]).includes(slugs.type))
      ? (slugs as NavigationControlModels.ModalityIdPathModel).id || null
      : null;
  }

  //</editor-fold>

  //<editor-fold desc="Stain">

  @Selector([NavigationControlState.slugs$])
  static stainIdSlug$(slugs: NavigationControlModels.PathModel | null) {
    return slugs && ((<string[]>[NavigationControlModels.PathModelType.enum.STAIN_ID]).includes(slugs.type))
      ? (slugs as NavigationControlModels.StainIdPathModel).id || null
      : null;
  }

  //</editor-fold>

  @Selector([NavigationControlState])
  static currentUrl$(state: NavigationControlStateModel): string {
    return state.currentURL;
  }
  
  @Selector([NavigationControlState])
  static queryParams$(state: NavigationControlStateModel): Params | null {
    return state.queryParams;
  }

  @Selector([NavigationControlState])
  static showSearch$(state: NavigationControlStateModel): boolean {
    return state.showSearch;
  }

  @Selector([NavigationControlState])
  static showClose$(state: NavigationControlStateModel): boolean {
    return state.showClose;
  }

  @Selector([NavigationControlState])
  static isPrinting$(state: NavigationControlStateModel): boolean {
    return state.isPrinting;
  }

  @Selector([NavigationControlState])
  static quizActive$(state: NavigationControlStateModel): boolean {
    return state.slugs instanceof NavigationControlModels.TrainingPathModel;
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  @Action(NavigationControlActions.SetActivatedRoute)
  setActivatedRoute({patchState}: StateContext<NavigationControlStateModel>,
                    {activatedRoute}: NavigationControlActions.SetActivatedRoute) {
    let slugNames: string[];
    let slugs: NavigationControlModels.PathModel;
    let currentURL = activatedRoute.url.map(segment => segment.path).join('/');
    switch (activatedRoute.data['pathModelType'] || NavigationControlModels.PathModelType.enum.DEFAULT) {
      case NavigationControlModels.PathModelType.enum.PAGE:
        slugNames = ['pageSlug'];
        slugs = new NavigationControlModels.PagePathModel();
        break;
      case NavigationControlModels.PathModelType.enum.NODE_ID:
        slugNames = ['idSlug'];
        slugs = new NavigationControlModels.NodeIdPathModel();
        break;
      case NavigationControlModels.PathModelType.enum.SLIDE_ID:
        slugNames = ['idSlug'];
        slugs = new NavigationControlModels.SlideSlideIdPathModel();
        break;
      case NavigationControlModels.PathModelType.enum.SLIDE:
        slugNames = ['eLearningSlug', 'subjectSlug', 'chapterSlug', 'blockSlug', 'slideSlug'];
        slugs = new NavigationControlModels.SlidePathModel();
        break;
      case NavigationControlModels.PathModelType.enum.TRAINING:
        slugNames = ['eLearningSlug', 'subjectSlug', 'chapterSlug', 'blockSlug', 'slideSlug'];
        slugs = new NavigationControlModels.TrainingPathModel();
        // Exclude quiz from loading path
        currentURL = activatedRoute.url.map(segment => segment.path).filter((v, i) => i != 0).join('/');
        break;
      case NavigationControlModels.PathModelType.enum.USER_ID:
        slugNames = ['idSlug'];
        slugs = new NavigationControlModels.UserIdPathModel();
        break;
      case NavigationControlModels.PathModelType.enum.MODALITY_ID:
        slugNames = ['idSlug'];
        slugs = new NavigationControlModels.ModalityIdPathModel();
        break;
      case NavigationControlModels.PathModelType.enum.STAIN_ID:
        slugNames = ['idSlug'];
        slugs = new NavigationControlModels.StainIdPathModel();
        break;
      case NavigationControlModels.PathModelType.enum.COPYRIGHT_ID:
        slugNames = ['idSlug'];
        slugs = new NavigationControlModels.CopyrightIdPathModel();
        break;
      default:
        slugNames = [];
        slugs = new NavigationControlModels.DefaultPathModel();
        break;
    }

    for (const slug of slugNames) {
      const value = activatedRoute.paramMap.get(slug);
      if (value) {
        (slugs as any)[slug.replace('Slug', '')] = activatedRoute.paramMap.get(slug);
      }
    }

    const queryParams = activatedRoute.queryParams;

    patchState({
      slugs,
      currentURL,
      queryParams
    });
  }

  //</editor-fold>
}
